import { useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { PerspectiveProvider, usePerspective } from "@/context/PerspectiveContext";
import EntryExperience from "@/components/EntryExperience";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import ProblemSection from "@/components/ProblemSection";
import Intro from "@/components/Intro";
import EditorialMarquee from "@/components/Marquee";
import Services from "@/components/Services";
import Approach from "@/components/Approach";
import Impact from "@/components/Impact";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";

function Site() {
  const { perspective, setPerspective } = usePerspective();
  const showEntry = perspective === null;
  const lenisRef = useRef(null);
  const prevPerspective = useRef(perspective);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = showEntry ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [showEntry]);

  useEffect(() => {
    if (prevPerspective.current && perspective && prevPerspective.current !== perspective) {
      lenisRef.current?.scrollTo(0, { immediate: true });
    }
    prevPerspective.current = perspective;
  }, [perspective]);

  return (
    <div className="App">
      <AnimatePresence>
        {showEntry && <EntryExperience onSelect={setPerspective} />}
      </AnimatePresence>
      <Navbar />
      <main key={perspective ?? "initial"}>
        <Hero />
        <ProblemSection />
        <Intro />
        <EditorialMarquee />
        <Services />
        <Approach />
        <Impact />
        <Contact />
      </main>
      <Footer />
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
}

function App() {
  return (
    <PerspectiveProvider>
      <Site />
    </PerspectiveProvider>
  );
}

export default App;
